import { useAuth } from '@clerk/clerk-react'
import * as React from 'react'
import { Component } from 'react'
import Button from '../../components/Button/Button'
import Loader from '../../components/Loader/Loader'
import { clearLocalStorage } from '../utility'
import { canAttemptChunkAutoReload, performChunkAutoReload } from './chunkErrorRecovery'
import { getFriendlyErrorCopy, isChunkLoadRecoveryError } from './errorBoundaryCopy'

const ChunkRecoveryUI = React.memo(() => (
  <div
    className="flex flex-col items-center justify-center gap-4 min-h-screen bg-BG-Default-utility-gray-00 fixed top-0 left-0 right-0 bottom-0 z-99999"
    data-testid="chunk-recovery-loader"
  >
    <Loader />
    <p className="text-Text-medium-06 text-sm">Updating the app…</p>
  </div>
))

ChunkRecoveryUI.displayName = 'ChunkRecoveryUI'

const ErrorFallbackUI = React.memo(({ error, onReset }) => {
  const { signOut } = useAuth()
  const friendly = getFriendlyErrorCopy(error)

  /**
   * Signs out and redirects user when boundary copy allows logout action.
   * @returns {Promise<void>}
   */
  const handleLogout = async () => {
    try {
      clearLocalStorage()
      await signOut()
      window.location.href = '/'
    } catch (signOutError) {
      console.error('Error signing out:', signOutError)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-BG-Default-utility-gray-00 fixed top-0 left-0 right-0 bottom-0 z-99999">
      <div className="text-center p-8 bg-BG-Default-white rounded-lg shadow-lg w-[30%]">
        <h2 className="text-2xl font-bold mb-4">{friendly.title}</h2>
        <div className="text-Text-medium-06 mb-6">{friendly.body}</div>
        {(import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'development') &&
          error?.message && (
            <div className="text-left text-xs text-Text-medium-06 mb-6 p-3 rounded bg-BG-Default-neutral-00 break-words">
              {error.message}
            </div>
          )}
        <div className="flex justify-between gap-8">
          {friendly.showReload && (
            <Button type="primary" className="px-4 py-2 flex-1" onClick={onReset} text="Reload" />
          )}
          {friendly.showLogout && (
            <Button
              type="warning"
              className="px-4 py-2 flex-1"
              onClick={handleLogout}
              text="Logout"
            />
          )}
        </div>
      </div>
    </div>
  )
})

ErrorFallbackUI.displayName = 'ErrorFallbackUI'

class ErrorBoundaryClass extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, isAutoReloadingChunk: false }
  }

  static getDerivedStateFromError(error) {
    const isAutoReloadingChunk = isChunkLoadRecoveryError(error) && canAttemptChunkAutoReload()

    return { hasError: true, error, isAutoReloadingChunk }
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)

    if (this.state.isAutoReloadingChunk) {
      performChunkAutoReload()
    }
  }

  handleReset = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isAutoReloadingChunk) {
        return <ChunkRecoveryUI />
      }

      return (
        this.props.fallback || (
          <ErrorFallbackUI error={this.state.error} onReset={this.handleReset} />
        )
      )
    }

    return this.props.children
  }
}

export default ErrorBoundaryClass
